/**
 * 데이터 수집 흐름 화면 셸 (디자인: C:\tmp\smart_injection_monitor_UX\데이터 수집흐름\*.png).
 *
 *  ┌ 헤더: 전사 › [토폴로지 뷰 ▾ / 조감도 뷰] · 요약 지표 · 시각 · 새로 고침 ┐
 *  │ 토폴로지 뷰: [설비 노드 검색 | 그래프 + 이벤트 로그 | 파이프라인 요약 / 노드 상세] │
 *  │ 조감도 뷰  : 공장 카드 (가동률·설비 상태·알람·환경)                         │
 *
 * - 마운트 시 PipelineFeed 기동(백엔드 /dashboard/pipeline/snapshot 5초 폴링), 언마운트 시 정지. 두 보기가 같은 피드를 쓴다.
 *   API 실패 시 마지막 스냅샷을 유지하고 오류 배너를 띄운다. 가짜 데이터로 대체하지 않는다.
 * - 보기는 URL ?view=aerial 로 고른다(기본 토폴로지). 새로 고침·북마크에도 유지된다.
 * - 단축키: Esc = 노드 선택 해제.
 */
import { Suspense, lazy, useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PipelineFeed } from "../../lib/pipeline-feed";
import { fetchPipelineSnapshot } from "../../../services/api";
import { selectActions, selectError, selectFeedStatus, selectMode, selectNodes, usePipelineStore, } from "../../store/pipeline-store";
import { DashboardHeader } from "./DashboardHeader";
import { EventLogPanel } from "./EventLogPanel";
import { NodeDetailPanel } from "./NodeDetailPanel";
import { SourceSidebar } from "./SourceSidebar";
import { AerialView } from "../aerial/AerialView";

const PipelineCanvasLazy = lazy(() => import("../flow/PipelineCanvas"));

function CanvasMessage({ children, onRetry }) {
    return (<div className="dfv-canvas-msg">
      <span className="material-symbols-outlined" aria-hidden="true">account_tree</span>
      <div>{children}</div>
      {onRetry && (<button type="button" className="dfv-btn dfv-btn--ghost" onClick={onRetry}>
          <span className="material-symbols-outlined" aria-hidden="true">refresh</span>다시 시도
        </button>)}
    </div>);
}

function ErrorBanner({ message, onRetry, onDismiss }) {
    return (<div role="alert" className="dfv-error-banner">
      <span className="material-symbols-outlined" aria-hidden="true">error</span>
      <span className="dfv-error-banner__msg" title={message}>{message}</span>
      <button type="button" className="dfv-btn dfv-btn--ghost" onClick={onRetry}>재시도</button>
      <button type="button" className="dfv-icon-btn" aria-label="배너 닫기" title="배너 닫기" onClick={onDismiss}>
        <span className="material-symbols-outlined" aria-hidden="true">close</span>
      </button>
    </div>);
}

function TopologyView({ onRetry }) {
    const nodes = usePipelineStore(selectNodes);
    const mode = usePipelineStore(selectMode);
    const feedStatus = usePipelineStore(selectFeedStatus);
    const error = usePipelineStore(selectError);
    const [bannerDismissedFor, setBannerDismissedFor] = useState(null);

    const loading = mode === "idle" && nodes.length === 0 && feedStatus !== "error";
    const empty = mode !== "idle" && nodes.length === 0;
    const showError = !!error && feedStatus === "error" && bannerDismissedFor !== error;

    return (<div className="dfv-topology">
      <SourceSidebar />
      <div className="dfv-center">
        <main className="dfv-panel dfv-canvas" aria-label="파이프라인 그래프">
          {loading ? (<CanvasMessage>첫 스냅샷 대기 중…</CanvasMessage>)
            : empty ? (<CanvasMessage onRetry={onRetry}>표시할 파이프라인 노드가 없습니다. DB 연결 상태를 확인하세요.</CanvasMessage>)
            : nodes.length === 0 ? (<CanvasMessage onRetry={onRetry}>스냅샷을 받지 못했습니다.</CanvasMessage>)
            : (<Suspense fallback={<CanvasMessage>그래프 모듈 불러오는 중…</CanvasMessage>}>
                <PipelineCanvasLazy />
              </Suspense>)}
          {showError && (<ErrorBanner message={`스냅샷 API 오류: ${error} — 마지막 화면을 유지하며 5초마다 다시 시도합니다.`} onRetry={() => { setBannerDismissedFor(null); onRetry(); }} onDismiss={() => setBannerDismissedFor(error)}/>)}
        </main>
        <EventLogPanel />
      </div>
      <NodeDetailPanel />
    </div>);
}

export function DataFlowDashboard() {
    const [searchParams, setSearchParams] = useSearchParams();
    const view = searchParams.get("view") === "aerial" ? "aerial" : "topology";
    const { selectNode } = usePipelineStore(selectActions);
    const feedRef = useRef(null);
    const [refreshSeq, setRefreshSeq] = useState(0);

    // 실데이터 피드 시작/정지. 백엔드 스냅샷을 5초마다 받아 화면 스냅샷으로 조립한다.
    useEffect(() => {
        const { applySnapshot, recordFailure } = usePipelineStore.getState().actions;
        const feed = new PipelineFeed({
            fetchSnapshot: fetchPipelineSnapshot,
            onSnapshot: (snap, meta) => applySnapshot(snap, meta),
            onError: (message) => recordFailure(message),
        });
        feedRef.current = feed;
        feed.start();
        return () => {
            feed.stop();
            feedRef.current = null;
        };
    }, []);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape")
                selectNode(null);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [selectNode]);

    const refresh = useCallback(() => {
        feedRef.current?.refresh();
        setRefreshSeq((n) => n + 1); // 조감도 뷰도 다시 받는다
    }, []);
    const changeView = useCallback((next) => {
        const params = new URLSearchParams(searchParams);
        if (next === "aerial")
            params.set("view", "aerial");
        else
            params.delete("view");
        setSearchParams(params, { replace: true });
    }, [searchParams, setSearchParams]);

    return (<div className="dfv-screen">
      <DashboardHeader view={view} onViewChange={changeView} onRefresh={refresh}/>
      {view === "aerial" ? <AerialView refreshSeq={refreshSeq}/> : <TopologyView onRetry={refresh}/>}
    </div>);
}
export default DataFlowDashboard;
